import React, { useState, useEffect, useCallback, useRef } from "react";
import Paginate from "../../../CommonComponents/Pagination/Paginate";
import customAPIRequest from "../../../../utis/customAPIRequest";
import { Container } from "react-bootstrap";
import styles from './KdfListingPage.module.css';
import { apiJsonRequest } from "../../../../utis/apiRequest";
import CommonModal from "../../../Modals/CommonModal";
import Button from "../../../Elements/Button";
import { networkService } from "../../../config/axios";
import KdfFilerForm from "./KdfFIlters";
import LoadingScreen from "../../../Elements/LoadingScreen/LoadingScreen";
import { useLocation } from "react-router-dom";
import KdfListCard from "./KdfListCard";

const KdfListPage = ({ props }) => {
    const itemsPerPage = 10;
    const [docData, setDocData] = useState({ loading: true });
    const [getDeleteKDFItem, setDeleteKDFItem] = useState(null);
    const [getCloneKDFItem, setCloneKDFItem] = useState(null);
    const [getPrivateFlagKDFItem, setPrivateFlagKDFItem] = useState(null);
    const { pathname } = useLocation();
    const [currentFilters, setCurrentFilters] = useState({});
    const debounceTimeout = useRef(null);
    const isFetchingRef = useRef(false);
    const initialLoadRef = useRef(false);

const fetchKDFData = useCallback(
    (page = 1, filters = currentFilters) => {
        // Prevent duplicate calls
        if (isFetchingRef.current) {
            return;
        }
        
        const payload = {
            page,
            per_page: itemsPerPage,
            ...filters,
        };

        if (pathname.includes('kdfs/token')) {
            payload.doc_id = window?.objDoc?.id;
            payload.name = window?.objDoc?.title;
        }

        setDocData(prev => ({ ...prev, loading: true }));
        localStorage.setItem("currentPage", page);
        
        isFetchingRef.current = true;
        customAPIRequest('docs-search.json', 'POST', payload)
            .then((res) => {
                setDocData({ ...res, page, per_page: itemsPerPage, loading: false });
            })
            .catch(() => {
                setDocData({ error: "Something went wrong", loading: false });
            })
            .finally(() => {
                isFetchingRef.current = false;
            });
    },
    [itemsPerPage, pathname, currentFilters]
);

    const handleApplyFilter = useCallback((filters = {}) => {
        if (JSON.stringify(filters) === JSON.stringify(currentFilters)) return;
        setCurrentFilters(filters);

        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(() => {
            fetchKDFData(1, filters);
        }, 300);
    }, [currentFilters, fetchKDFData]);

    const handleUpdateDocData = async (dataToUpdate = [], itemID, skipRefresh = false) => {
        try {
            await apiJsonRequest(`kdfs/${itemID}.json`, 'PATCH', { doc: { tag_ids: dataToUpdate } });
            // Skip refresh for tag updates to avoid loader flash
            if (!skipRefresh) {
                fetchKDFData(docData?.page ?? 1, currentFilters, { force: true });
            }
        } catch (error) {
            console.warn(error);
        }
    };

    const handleAddKDFTagsEvent = async (value, data) => {
        let valueToUpdate = [];
        if (value.length > data?.tags.length) {
            const updateTagsToDoc = await apiJsonRequest(`tags.json`, 'POST', { name: value[value.length - 1] });
            valueToUpdate = [...data?.tags?.map(item => item.id), updateTagsToDoc.data.id];
        } else {
            valueToUpdate = data?.tags.filter(item => value.includes(item.name))?.map(item => item.id);
        }
        // Skip refresh to avoid loader - tags are updated optimistically in the UI
        handleUpdateDocData(valueToUpdate, data.id, true);
    }

    const updateRecordData = (value, data, type) => {
        if (type === 'tags') {
            handleAddKDFTagsEvent(value, data);
        }
    }

    const handleDeleteKDFItemEvent = async () => {
        const { status } = await networkService(`/kdfs/${getDeleteKDFItem}.json`, 'DELETE');
        if (status === 204) {
            fetchKDFData();
            setDeleteKDFItem(null);
        }
    }

    const handleCloneKDFEvent = async () => {
        const { data } = await networkService(`/docs/clone/${getCloneKDFItem}.json`, 'GET');
        if (data.msg === 'ok') {
            fetchKDFData();
            setCloneKDFItem(null);
        }
    }

    const handleMakePrivateEvent = async () => {
        const { status } = await networkService(`kdfs/${getPrivateFlagKDFItem.id}.json`, 'PATCH', {
            doc_id: getPrivateFlagKDFItem.id,
            private_flag: getPrivateFlagKDFItem?.status ?? false,
        });
        if (status === 200) {
            fetchKDFData();
            setPrivateFlagKDFItem(null);
        }
    }

    useEffect(() => {
      // Prevent duplicate initial load calls (React StrictMode)
      // Use local ref to handle StrictMode double-invocation
      // The global deduplication in customAPIRequest will handle simultaneous calls
      if (initialLoadRef.current || isFetchingRef.current) {
        return;
      }
      initialLoadRef.current = true;
      const savedPage = Number(localStorage.getItem("currentPage")) || 1;
      fetchKDFData(savedPage);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Expose refresh function for Editor to call when closing
    useEffect(() => {
      window.refreshKDFList = () => {
        const currentPage = Number(localStorage.getItem("currentPage")) || 1;
        fetchKDFData(currentPage);
      };

      return () => {
        delete window.refreshKDFList;
      };
    }, [fetchKDFData]);

    return (
        <div>
            {/* Modals */}
            <CommonModal
                title={getDeleteKDFItem != null ? 'Delete KDF' : 'Clone this KDF'}
                modalStatus={getDeleteKDFItem != null || getCloneKDFItem != null}
                changeActiveStatus={() => {
                    setDeleteKDFItem(null);
                    setCloneKDFItem(null);
                }}
                centered
                classList={'upload-kdf-modal-wrapper alert-modal'}
                footer={
                    <div className="d-flex m-auto">
                        <Button text={'Cancel'} classList={'alert delete-btn'} addonClass={'my-2 mx-2'} onClick={() => {
                            setDeleteKDFItem(null);
                            setCloneKDFItem(null);
                        }} />
                        <Button text={getDeleteKDFItem != null ? 'Delete' : 'Clone'} classList={'success delete-btn'} addonClass={'my-2 mx-2 common-modal-yes-button'} onClick={() => {
                            getDeleteKDFItem != null ? handleDeleteKDFItemEvent() : handleCloneKDFEvent();
                        }} />
                    </div>
                }
            >
                <p style={{ fontSize: '14px' }}>
                    {
                        getDeleteKDFItem != null
                            ? 'Are you sure you want to delete this KDF? This action cannot be undone.'
                            : 'Do you want to continue?'
                    }
                </p>
            </CommonModal>

            <CommonModal
                title={''}
                modalStatus={getPrivateFlagKDFItem != null}
                changeActiveStatus={() => setPrivateFlagKDFItem(null)}
                centered
                classList={'upload-kdf-modal-wrapper alert-modal'}
                footer={
                    <div className="d-flex m-auto">
                        <Button text={'Cancel'} classList={'alert delete-btn'} addonClass={'my-2 mx-2'} onClick={() => setPrivateFlagKDFItem(null)} />
                        <Button text={'Yes'} classList={'success delete-btn'} addonClass={'my-2 mx-2 common-modal-yes-button'} onClick={() => handleMakePrivateEvent()} />
                    </div>
                }
            >
                <p style={{ fontSize: '14px' }}>
                    Hide this KDF from other kinetizine users. Your recipients will still be able to view it via the LaserLINX you share with them.
                </p>
            </CommonModal>

            <div className="header-content-main">
                <div className="doc-filter">
                    {/* <h4 className="filter-title">Filters</h4> */}
                    <KdfFilerForm
                        triggerApplyFilterEvent={handleApplyFilter}
                        loading={docData?.loading}
                        kdfTitleDefault={pathname.includes('kdfs/token') ? window?.objDoc?.title : ''}
                    />
                </div>
            </div>

            <Container className={styles.listingContainer}>
                {docData?.loading ? (
                    <LoadingScreen />
                ) : docData?.data?.docs?.length > 0 ? (
                    <>
                        {docData?.data?.docs.map((kdf, index) => (
                            <KdfListCard
                                key={`${kdf?.id}-${index}`}
                                data={kdf}
                                triggerUdateRecord={() => fetchKDFData()}
                                triggerUpdateRecordData={updateRecordData}
                                time_zone_list={props.time_zone_list}
                                triggerDeleteKDF={setDeleteKDFItem}
                                triggerCloneKDF={setCloneKDFItem}
                                triggerMakePrivateEvent={setPrivateFlagKDFItem}
                            />
                        ))}
                        {docData?.data?.cnt > docData?.per_page && (
                            <Paginate
                                currentPage={docData?.page}
                                totalPages={Math.ceil(docData?.data?.cnt / docData?.per_page)}
                                onPageChange={(page) => fetchKDFData(page)}
                                totalData={docData?.data?.cnt}
                                itemTitle={docData?.data?.cnt > 1 ? "KDFs" : "KDF"}
                                pageSize={docData?.per_page}
                                currentCount={docData?.data?.docs?.length}
                            />
                        )}
                    </>
                ) : (
                    docData?.error || "No Record Found"
                )}
            </Container>
        </div>
    );
};

export default KdfListPage;
